import WarningIcon from "@mui/icons-material/Warning";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Link,
  Menu,
  MenuItem,
  SxProps,
  Tooltip,
  Typography
} from "@mui/material";
import { push } from "connected-react-router";
import * as React from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { connect, useDispatch, useSelector } from "react-redux";
import moment from "moment";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useLocation } from "react-router";
import * as billActions from "../../../actions/bill";
import * as NotificationActions from "../../../actions/notification";
import OkhatiDialog from "../../../components/Dialog/Dialog";
import Modal from "../../../components/Modal/Modal";
import { t, tl } from "../../../components/translate";
import useIsAccountSubscribed from "../../../hooks/accounts";
import useTaxations from "../../../hooks/useTaxations";
import {
  BillDocumentType,
  BillItemSource,
  StockUnitTypes
} from "../../../interfaces/BillInterfaces";
import { AppDispatch, RootState } from "../../../store";
import Can from "../../Policy/Can";
import { getTaxationOptions } from "../../Stock/StockCreateEdit/CreateStockProduct";
import { BILL_EDITOR_WARNING_TYPE, LOCALSTORAGE_BILL } from "./BillEditor";
import { paymentOptionsEnum } from "./BillSummary";
import BillDraftPrint from "./PrintPreview/DraftBill";
import { ACCIDENT_SUB_PRODUCT, MEDICAL_SUB_PRODUCT, SCHEME_TYPE } from "../../../../map/ssf";
import mixpanelAnalytics from "../../../mixpanel-analytics/mixpanelAnalytics";
import EVENT from "../../../mixpanel-analytics/event";
import { setWarning } from "../../../actions/bill";
import useCanPayWithQr from "../../../hooks/useCanPayWithQr";
import QrPaymentPanel from "./Payment/QrPaymentPanel";
import NepalPayQrPanel from "./Payment/NepalPay/NepalPayQrPanel";
import { useCurrentStore } from "../../../hooks/useCurrentStore";
import { useCurrentCounter } from "../../../hooks/useCurrentCounter";

// @ts-expect-error - css
import styles from "./BillEditor.module.css";
import { TQrPaymentInfo } from "../../../interfaces/QrPayment";
import { ModeOfPaymentMappedToGl } from "../../../interfaces/ResourceCentreInterface";
import { TaxationInterface } from "../../../interfaces/ProductInterface";
import { getBillItemKeyForCreditbleQuantity } from "../../../actions/helpers/billHelper";
import { updateBillingPackage } from "../../../slices/billPackageSlice";
import { useAppSelector } from "../../../store/hooks";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { FonepayDynamicQrPanel } from "./Payment/Fonepay/FonepayQrPanel";

interface ErrorProps {
  [key: string]: string;
}

enum Errors {
  TAXATION = "taxation"
}

const hasTaxationRuleMappedWithLedger = (item, salesTaxationRules) =>
  salesTaxationRules.find((taxRule) => taxRule.id === item.salesTaxationId)?.ledgerId;

const getLedgerMappingError = (
  billData: BillDocumentType,
  rcMappedPaymentMethods: ModeOfPaymentMappedToGl,
  salesTaxationOptions: Array<TaxationInterface>
): ErrorProps[] => {
  const errors = [] as ErrorProps[];

  // client and referrer check
  if (billData.client?.id && !billData.client?.ledgerId) {
    errors.push({ message: "Client is Not Mapped with ledger." });
  }
  if (billData.referrerId && !billData.referrerLedgerId) {
    errors.push({ message: "Referrer is Not Mapped with ledger." });
  }

  // payment method check
  const paymentBreakdown = billData.paymentInfo.paymentBreakdown || [];

  paymentBreakdown.forEach((p) => {
    const { method } = p;
    // Skip balance/noPayment
    if (
      method !== paymentOptionsEnum.balance &&
      method !== paymentOptionsEnum.noPayment &&
      !rcMappedPaymentMethods[method]?.ledgerId
    ) {
      errors.push({ message: `Payment Method "${method}" is not Mapped with ledger.` });
    }
  });

  // bill items check
  if (billData.billItems.some((item) => !item.salesLedgerId)) {
    errors.push({ message: "Bill Item is not mapped with ledger" });
  }

  // sales taxation check
  const serviceNotMappedWithTaxation = billData.billItems.some(
    (item) => item?.salesTaxationId && !hasTaxationRuleMappedWithLedger(item, salesTaxationOptions)
  );

  if (serviceNotMappedWithTaxation) {
    errors.push({ message: "Sales Taxation is not mapped with ledger.", key: Errors.TAXATION });
  }
  return errors;
};

export const isRemarksValid = (draft: BillDocumentType): boolean => {
  if (!draft.settings.remarksCompulsoryForDiscountedBill) return true;

  const hasDiscount = !!draft.summary.discount || !!draft.summary.discountPct;
  const strippedRemarks = draft.remarks.replace(/<[^>]*>/g, "").trim();

  return hasDiscount ? strippedRemarks.length > 0 : true;
};

export const hasValidRichTextRemark = (remarks: string | undefined): boolean => {
  if (!remarks) return false;

  const strippedRemarks = remarks.replace(/<[^>]*>/g, "").trim();
  return strippedRemarks.length > 0;
};

export const isSsfSchemeValidAndBalanceEnough = (
  draft: BillDocumentType
): { isInvalid: boolean; message: string } => {
  if (draft.ssf?.id) {
    const { scheme, balance, subProduct } = draft.ssf;
    switch (scheme.value) {
      case SCHEME_TYPE.MEDICAL.value:
        if (!balance.medical.isActive) {
          return { isInvalid: true, message: "Medical scheme is not active" };
        }
        if (
          subProduct.value === MEDICAL_SUB_PRODUCT.OPD.value &&
          draft.summary.totalAmount > balance.medical.opd
        ) {
          return { isInvalid: true, message: "OPD balance not enough" };
        }
        if (
          subProduct.value === MEDICAL_SUB_PRODUCT.IPD.value &&
          Number(balance.medical.opd) + Number(balance.medical.ipd) < draft.summary.totalAmount
        ) {
          return { isInvalid: true, message: "IPD balance not enough" };
        }
        break;
      case SCHEME_TYPE.ACCIDENT.value:
        if (!balance.accident.isActive) {
          return { isInvalid: true, message: "Accident scheme is not active" };
        }
        if (
          subProduct.value === ACCIDENT_SUB_PRODUCT.NON_WORK_RELATED.value &&
          draft.summary.totalAmount > balance.accident.value
        ) {
          return { isInvalid: true, message: "Non work related balance not enough" };
        }

        if (
          subProduct.value === ACCIDENT_SUB_PRODUCT.WORK_RELATED.value &&
          !draft.ssf.employer?.eSSID
        ) {
          return { isInvalid: true, message: "Employer is mandatory for accident scheme" };
        }
        break;
      default:
        return { isInvalid: false, message: "" };
    }
  }
  return { isInvalid: false, message: "" };
};

interface BillActionsProps {
  draft: BillDocumentType;
  isCreditNote: boolean;
  creditableQuantities?: Record<string, number>;
  isUsedInQrPanel?: boolean;
  actions: {
    saveAsDraft: (draft: any, setLoading: (value: boolean) => void) => Promise<void>;
    saveAsProformaBill: () => Promise<void>;
    cancelDraft: () => void;
    finaliseAsBill: (draft: any, openPrintWindow: any) => Promise<void>;
  };
  sxProps?: SxProps;
}

const BillActions = ({
  draft,
  actions,
  isCreditNote,
  creditableQuantities,
  isUsedInQrPanel = false,
  sxProps = {}
}: BillActionsProps) => {
  // hooks
  const { currentStore } = useCurrentStore();
  const { currentCounter } = useCurrentCounter();
  const resourceCentre = useCurrentResourceCentre();

  const dispatch = useDispatch();
  const taxations = useTaxations();
  const isAccountSubscribed = useIsAccountSubscribed();
  const {
    canPayWithNepalPayDynamicQr,
    canPayWithFonepayDynamicQr,
    canPayWithEsewaQr,
    canPayWithFonepayQr,
    canPayWithKhaltiQr,
    canPayWithBankQr,
    canPayWithStaticQr
  } = useCanPayWithQr();

  // selectors
  const billingPackageDraft = useAppSelector((state) => state.billingPackages.draft);
  const rcId = useSelector((state: RootState) => state.userContext.resourceCentreId);
  const rcSubscriptionFeatures =
    useSelector((s: RootState) => s.resources.resourceCentres[0]?.subscriptions?.features) || {};
  const rcSettings = useSelector((s: RootState) => s.resources.resourceCentres[0]?.settings) || {};
  const rcMappedPaymentMethods = useSelector(
    (state: RootState) =>
      state.resources.resourceCentres[0]?.settings?.accountSettings?.modeOfPayment || {}
  );

  // local states
  const [loading, setLoading] = React.useState(false);
  const [openQrPaymentPanel, setOpenQrPaymentPanel] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [qrAnchorEl, setQrAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [openFonepayDynamicQrPanel, setOpenFonepayDynamicQrPanel] = React.useState(false);
  const [openNepalPayDynamicQrPanel, setOpenNepalPayynamicQrPanel] = React.useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<
    keyof TQrPaymentInfo | null
  >(null);
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<ErrorProps[]>([]);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const isFromBillingPackage = params.get("isFromBillingPackage");
  const [reminderDialogState, setReminderDialogState] = React.useState({
    show: false,
    message: "",
    openPrintWindow: false
  });
  const [issueDateDialogState, setIssueDateDialogState] = React.useState({
    show: false,
    openPrintWindow: false
  });

  const { sms, email } = rcSubscriptionFeatures;
  const { smsSettings, emailSettings } = rcSettings;
  const { salesTaxationOptions } = getTaxationOptions(taxations);

  // settings
  const isCounterBillingEnabled = rcSettings?.billingSettings?.enableCounterBilling || false;
  const allowServiceAndStockItemOnlyInBill =
    rcSettings?.billingSettings?.allowServiceAndStockItemOnlyInBill || false;
  const requireSpInService = resourceCentre?.settings?.billingSettings?.requireSpInService ?? false;
  const requireReferrer =
    resourceCentre?.settings?.globalSettings?.referralSettings?.requireReferrer ?? false;
  const requireExtraReferrer =
    resourceCentre?.settings?.globalSettings?.referralSettings?.requireExtraReferrer ?? false;

  const isTenderAmountValid =
    !draft.paymentInfo.tenderAmount ||
    isCreditNote ||
    draft.paymentInfo.tenderAmount === 0 ||
    draft.paymentInfo.tenderAmount >= draft.summary.totalAmount;

  let savable = Boolean(draft.client?.id && (draft.isProformaBill || draft.billNumber));

  if (isCreditNote) {
    savable = savable && draft.billItems.filter((item) => item.returned).length > 0;
  }

  // functions
  const hasInvalidProductQuantity = () => draft.billItems.some((item) => item.quantity === 0);
  const hasNoProductSelected = () =>
    draft?.billItems?.find((item) => !item.productId && !item.description?.trim());

  const hasNoNameProductSelected = () =>
    draft?.billItems?.some((item) => !item.description?.trim());

  const getBillingActionText = (selectedDraft: BillDocumentType, creditNote: boolean) => {
    if (selectedDraft.id) return tl("billing.updateDraft");
    if (creditNote) return tl("billing.cancel");
    return tl("billing.saveAsDraft");
  };

  const hasInvalidProductPackage = () =>
    draft.billItems
      .reduce((allItems, item) => (item.subItems ? [...allItems, ...item.subItems] : allItems), [])
      .some((subItem) => subItem.delivered === false);

  // for credit note, validate quantity before finalising the invoice
  const isQuantityValid = () => {
    const errorMessages: string[] = [];

    // at least one item should have a quantity greater than 0
    if (!draft.billItems.some((item) => Math.abs(item.quantity ?? 0) > 0)) {
      dispatch(
        setWarning({
          message: "At least one item should have a quantity greater than 0 to create credit note.",
          type: null
        })
      );
      return false;
    }

    // if bill has same item twice or more
    // form a map with combination of productId and batchId as key and sum of quantities as value
    // and check if any item has quantity greater than the sum
    const billProductQuantityMap = draft.billItems.reduce((acc, item) => {
      const mapKey = getBillItemKeyForCreditbleQuantity(acc);

      if (!mapKey) {
        dispatch(
          setWarning({
            message: "Something went wrong! Product map key is not available",
            type: null
          })
        );
        return {};
      }

      const quantity =
        item.stockUnitType === StockUnitTypes.unit
          ? Math.abs(item?.quantity || 0)
          : Math.abs(item?.quantity || 0) * (item?.stockProductUnitsPerPackage || 1);

      // eslint-disable-next-line no-param-reassign
      acc[mapKey] = (acc[mapKey] || 0) + quantity;
      return acc;
    }, {});

    const processedProductIds = new Set();

    draft.billItems.forEach((item) => {
      const mapKey = getBillItemKeyForCreditbleQuantity(item);
      const batchId = item?.batchInfo?.batchId;
      const productName = item?.description || item?.productData?.name;

      if (
        mapKey &&
        creditableQuantities &&
        creditableQuantities[mapKey] &&
        !processedProductIds.has(mapKey)
      ) {
        const intendedQuantity: number = billProductQuantityMap[mapKey] || 0;
        const availableQuantity: number = creditableQuantities[mapKey] || 0;

        if (intendedQuantity > availableQuantity) {
          if (availableQuantity <= 0) {
            errorMessages.push(
              `The item "${productName}" has already been fully credited and cannot be credited any more.`
            );
          } else {
            errorMessages.push(
              batchId
                ? `The item "${productName}" with batch id ${batchId} can only be credited with a maximum quantity of ${availableQuantity} (in its smallest unit), but you are attempting to credit ${intendedQuantity}.`
                : `"${productName}" can only be credited with a maximum quantity of ${availableQuantity} (in smallest unit), but you are attempting to credit ${intendedQuantity}.`
            );
          }

          // Mark this product ID as processed to prevent duplicate messages
          processedProductIds.add(mapKey);
        }
      }
    });

    if (errorMessages.length) {
      const message = errorMessages.join(" ");
      dispatch(setWarning({ message, type: null }));
      return false;
    }

    // remove items with quantity 0 from draft -> billItems before sending to backend
    draft.billItems = draft.billItems.filter((item) => Math.abs(item.quantity ?? 0) > 0);

    return true;
  };

  // finalise invoice function
  const finaliseInvoice = async ({ openPrintWindow = false }) => {
    if (isCreditNote && !isQuantityValid()) {
      return;
    }

    if (!isCreditNote && hasInvalidProductQuantity()) {
      dispatch(setWarning({ message: "Product quantity cannot be 0.", type: null }));
      return;
    }

    if (isCounterBillingEnabled && !currentCounter) {
      dispatch(
        setWarning({
          message:
            "Counter wise billing is enabled, but counter is not set for billing on this machine. Please select a counter from settings.",
          type: BILL_EDITOR_WARNING_TYPE.COUNTER
        })
      );
      return;
    }

    const ssfChecks = isSsfSchemeValidAndBalanceEnough(draft);
    if (ssfChecks.isInvalid) {
      dispatch(
        setWarning({
          message: ssfChecks.message,
          type: null
        })
      );
      return;
    }

    if (draft?.isSsfBilling && !draft.ssf.id) {
      dispatch(
        setWarning({
          message: "SSF ID field cannot be empty. Please provide SSF ID",
          type: null
        })
      );
      return;
    }

    if (hasInvalidProductPackage()) {
      dispatch(
        setWarning({
          message: "Please remove undelivered package items inorder to finalise the invoice.",
          type: null
        })
      );
      return;
    }

    if (!isRemarksValid(draft)) {
      dispatch(
        setWarning({
          message: "Remarks is compulsory for discounted invoice.",
          type: null
        })
      );
      return;
    }

    if (isAccountSubscribed) {
      const ledgerMappingErrors = getLedgerMappingError(
        draft,
        rcMappedPaymentMethods,
        salesTaxationOptions
      );
      setErrors(ledgerMappingErrors);
      if (ledgerMappingErrors.length) {
        setOpenModal(true);
        return;
      }
    }

    dispatch(setWarning({ message: "", type: null }));
    setLoading(true);
    localStorage.removeItem(LOCALSTORAGE_BILL);

    if (draft?.isProformaBill) {
      /**
       * Put the proforma bill inside this function so we don't have to repeat the validation
       * If we extract away the above validations in a separate function,
       * we can always create separate funtions for proforma and finalise
       */
      await actions.saveAsProformaBill();
    } else {
      await actions.finaliseAsBill(draft, openPrintWindow);
    }
    setLoading(false);
  };

  const onDraftSave = React.useCallback(() => {
    if (isCreditNote) {
      actions.cancelDraft();
    }

    if (hasNoProductSelected()) {
      return dispatch(
        setWarning({ message: "Item is not selected for a row in the bill.", type: null })
      );
    }
    if (hasInvalidProductQuantity()) {
      return dispatch(setWarning({ message: "Product quantity cannot be 0.", type: null }));
    }
    localStorage.removeItem(LOCALSTORAGE_BILL);
    return actions.saveAsDraft(draft, setLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, isCreditNote, actions]);

  const onFinalizeBill = ({ openPrintWindow = false }) => {
    if (requireSpInService) {
      if (
        draft.billItems.some(
          (item) =>
            (item.source === BillItemSource.services ||
              item.productData?.source === BillItemSource.services) &&
            (item.serviceProviderIds ?? []).length === 0
        )
      ) {
        return dispatch(
          setWarning({
            message: `Service provider is required in each bill item of type ${BillItemSource.services}. Please turn the setting off if you don't want to enforce service provider in each bill item.`,
            type: null
          })
        );
      }
    }

    if (allowServiceAndStockItemOnlyInBill) {
      if (draft?.billItems.some((item) => !item.productId && !item.productData?.id)) {
        const invalidItems = draft.billItems.filter(
          (item) => !item.productId && !item.productData?.id
        );

        return dispatch(
          setWarning({
            message: `Bill items must be either of type 'Service' or 'Stock'. Please remove any invalid items like ${invalidItems
              .map((item) => item.description)
              .join(", ")}.`,
            type: null
          })
        );
      }
    }

    if (!isCreditNote && requireReferrer && !draft.referredBy) {
      return dispatch(
        setWarning({
          message: "Referrer is required. Please select a referrer to proceed.",
          type: null
        })
      );
    }

    if (!isCreditNote && requireExtraReferrer && !draft.extraReferrer) {
      return dispatch(
        setWarning({
          message: "Extra referrer is required. Please select an extra referrer to proceed.",
          type: null
        })
      );
    }

    if (hasNoProductSelected()) {
      return dispatch(
        setWarning({ message: "Item is not selected for a row in the bill.", type: null })
      );
    }

    if (hasNoNameProductSelected()) {
      return dispatch(
        setWarning({ message: "One of the item has no name. Please remove it first.", type: null })
      );
    }

    if (draft?.issueDate && !moment().isSame(draft.issueDate, "day")) {
      return setIssueDateDialogState({
        show: true,
        openPrintWindow
      });
    }

    if (draft?.remindOn?.duration > 0) {
      if (!sms.subscribed && !email.subscribed) {
        return setReminderDialogState({
          show: true,
          openPrintWindow,
          message:
            "The resource centre is not subscribed to SMS and email feature, Would you still like to proceed?"
        });
      }

      if (!smsSettings?.billReminder && !emailSettings?.billReminder) {
        return setReminderDialogState({
          show: true,
          openPrintWindow,
          message:
            "The resource centre has not subscribed to SMS and email for bill reminder feature, Would you still like to proceed?"
        });
      }

      if (smsSettings.billReminder && sms?.volumeLeft <= 0) {
        return setReminderDialogState({
          show: true,
          openPrintWindow,
          message:
            "Insufficient SMS balance to execute the transaction, Would you still like to proceed?"
        });
      }
    }

    if (billingPackageDraft?.id && isFromBillingPackage === "yes") {
      dispatch(updateBillingPackage({ data: billingPackageDraft, id: billingPackageDraft.id }));
    }

    return finaliseInvoice({ openPrintWindow });
  };

  // Overriding default behaviour of alt + d (focuses on browser's search tab by default)
  React.useEffect(() => {
    const handleShortcut = (event) => {
      if (event.altKey && event.key === "d") {
        event.preventDefault();
        event.stopPropagation();
        if (!loading) {
          onDraftSave();
        }
      }
    };
    window.addEventListener("keydown", handleShortcut);

    return () => {
      window.removeEventListener("keydown", handleShortcut);
    };
  }, [onDraftSave, loading]);

  useHotkeys(
    "alt + s, ctrl + p",
    (event, handler) => {
      event.preventDefault();
      if (loading) return;
      if (handler.key === "alt+s") {
        onFinalizeBill({ openPrintWindow: false });
      }
      if (handler.key === "ctrl+p") {
        onFinalizeBill({ openPrintWindow: true });
      }
    },
    { enableOnTags: ["INPUT", "SELECT", "TEXTAREA"] }
  );

  return (
    <>
      <Box
        sx={{
          height: "50px",
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          ...sxProps,
          ...(!isUsedInQrPanel && {
            width: {
              xs: "100%",
              md: "calc(100vw - 80px)"
            },
            paddingX: 2,
            position: "fixed",
            bottom: 0,
            zIndex: 10,
            backgroundColor: "#f1f1f1",
            boxShadow: "1px 1px 6px 0px #00000038",
            borderTop: 1,
            borderColor: "divider"
          }),
          ...(isUsedInQrPanel && {
            justifyContent: "flex-end"
          })
        }}
      >
        {!isUsedInQrPanel && (
          <Box
            display="flex"
            flexGrow={1}
            justifyContent="flex-start"
            alignItems="center"
            fontSize="16px"
          >
            <Typography>
              <Box component="span" fontWeight={500}>
                {tl("billing.total")}{" "}
              </Box>
              रू {draft.summary.totalAmount}
            </Typography>
          </Box>
        )}

        {/* right buttons */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {draft && (
            <BillDraftPrint billData={{ ...draft, document: draft }} buttonText="Print Preview" />
          )}

          {!draft?.isProformaBill && !isUsedInQrPanel && (
            <ButtonGroup color="primary" variant="contained" sx={{ ml: 2 }}>
              <Button
                disabled={
                  Boolean(hasNoProductSelected()) || loading || isCreditNote || !canPayWithEsewaQr
                }
                onClick={() => {
                  setOpenQrPaymentPanel?.(true);
                  setSelectedPaymentMethod("esewa");
                }}
              >
                Pay using eSewa
              </Button>

              <Button
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => setQrAnchorEl(e.currentTarget)}
                size="small"
                disabled={
                  Boolean(hasNoProductSelected()) ||
                  loading ||
                  isCreditNote ||
                  (!canPayWithStaticQr &&
                    !canPayWithNepalPayDynamicQr &&
                    !canPayWithFonepayDynamicQr)
                }
              >
                <ArrowDropDownIcon />
              </Button>

              <Menu
                anchorEl={qrAnchorEl}
                open={Boolean(qrAnchorEl)}
                onClose={() => setQrAnchorEl(null)}
              >
                <MenuItem
                  onClick={() => {
                    setOpenQrPaymentPanel?.(true);
                    setSelectedPaymentMethod("khalti");
                    setQrAnchorEl(null);
                  }}
                  disabled={!canPayWithKhaltiQr}
                >
                  Pay using Khalti Static QR
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setOpenQrPaymentPanel?.(true);
                    setSelectedPaymentMethod("fonepay");
                    setQrAnchorEl(null);
                  }}
                  disabled={!canPayWithFonepayQr}
                >
                  Pay using Fonepay Static QR
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setOpenQrPaymentPanel?.(true);
                    setSelectedPaymentMethod("bank");
                    setQrAnchorEl(null);
                  }}
                  disabled={!canPayWithBankQr}
                >
                  Pay using Bank Static QR
                </MenuItem>

                <MenuItem
                  disabled={!canPayWithNepalPayDynamicQr}
                  onClick={() => {
                    setOpenNepalPayynamicQrPanel(true);
                  }}
                >
                  Pay with Nepal Pay Dynamic QR
                </MenuItem>

                <MenuItem
                  disabled={!canPayWithFonepayDynamicQr}
                  onClick={() => {
                    setOpenFonepayDynamicQrPanel(true);
                  }}
                >
                  Pay with Fonepay Dynaimc QR
                </MenuItem>
              </Menu>
            </ButtonGroup>
          )}

          {!draft?.isProformaBill && draft.isDraft && (
            <Can policyAccessKey="bill:createDraft">
              <ButtonGroup color="primary" variant="contained" sx={{ ml: 2 }}>
                <Tooltip title="Alt + D" arrow sx={{ fontSize: "14px" }}>
                  <Button
                    data-testmation="billingSaveAsDraft"
                    disabled={loading || !isTenderAmountValid || (isCreditNote ? false : !savable)}
                    onClick={() => {
                      onDraftSave();
                      mixpanelAnalytics.track(EVENT.TRACK_USER_AND_PAYMENT_INFO, {
                        client: draft?.client,
                        paymentInfo: draft?.paymentInfo,
                        currentStore
                      });
                    }}
                  >
                    {getBillingActionText(draft, isCreditNote)}
                  </Button>
                </Tooltip>
                <Button onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
                  <ArrowDropDownIcon />
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                >
                  <MenuItem onClick={() => setAnchorEl(null)} sx={{ padding: "0px" }}>
                    <BillDraftPrint
                      billData={{ ...draft, document: draft }}
                      printAsEstimate
                      buttonText={t("billing.printAsEstimate")}
                      buttonProps={{
                        variant: "text"
                      }}
                    />
                  </MenuItem>
                </Menu>
              </ButtonGroup>
            </Can>
          )}

          {/* if the bill is a proforma bill */}
          {draft?.isProformaBill && (
            <Tooltip title="Alt + S" arrow sx={{ fontSize: "14px" }}>
              <LoadingButton
                variant="contained"
                color="primary"
                disabled={
                  !savable || !isTenderAmountValid || loading || draft.billItems.length <= 0
                }
                onClick={async () => {
                  onFinalizeBill({ openPrintWindow: false });
                }}
                loading={loading}
                data-testmation="saveProformaBill"
                className={styles.billActionBtn}
              >
                <span>{tl("billing.saveProformaBill")}</span>
              </LoadingButton>
            </Tooltip>
          )}

          {/* if the bill is not a proforma bill */}
          {!draft?.isProformaBill && draft.isDraft && (
            <>
              <Can policyAccessKey="bill:finalizeBill">
                <Tooltip title="Alt + S" arrow sx={{ fontSize: "14px" }}>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    disabled={
                      !savable || !isTenderAmountValid || loading || draft.billItems.length <= 0
                    }
                    onClick={async () => {
                      onFinalizeBill({ openPrintWindow: false });
                      mixpanelAnalytics.track(EVENT.TRACK_USER_AND_PAYMENT_INFO, {
                        client: draft?.client,
                        paymentInfo: draft?.paymentInfo
                      });
                    }}
                    loading={loading}
                    data-testmation="billingFinalizeInvoice"
                    className={styles.billActionBtn}
                  >
                    <span>{tl("billing.finaliseInvoice")}</span>
                  </LoadingButton>
                </Tooltip>
              </Can>

              <Can policyAccessKey="bill:finalizeBill">
                <Tooltip title="Ctrl + P" arrow sx={{ fontSize: "14px" }}>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    disabled={
                      !savable || !isTenderAmountValid || loading || draft.billItems.length <= 0
                    }
                    onClick={async () => {
                      onFinalizeBill({ openPrintWindow: true });
                      mixpanelAnalytics.track(EVENT.TRACK_USER_AND_PAYMENT_INFO, {
                        client: draft?.client,
                        paymentInfo: draft?.paymentInfo
                      });
                    }}
                    loading={loading}
                    data-testmation="billingFinalizeInvoiceAndPrint"
                    className={styles.billActionBtn}
                  >
                    <span>Finalise And Print</span>
                  </LoadingButton>
                </Tooltip>

                {/* modal */}
                <Modal
                  open={openModal}
                  title={
                    <Box alignItems="centre" display="flex">
                      <IconButton
                        sx={{
                          color: "#f3b500"
                        }}
                      >
                        <WarningIcon />
                      </IconButton>
                      <Typography marginTop="8px">Warning!</Typography>
                    </Box>
                  }
                  footer={
                    <Button onClick={() => setOpenModal(false)} variant="contained">
                      Ok
                    </Button>
                  }
                >
                  {errors.map((item, index) => (
                    <Typography key={item.message}>{`${index + 1}. ${item.message}`}</Typography>
                  ))}
                  {errors.some((error) => error.key === Errors.TAXATION) && (
                    <Link
                      component="button"
                      onClick={() =>
                        dispatch(
                          push(`/resourcecentres/${rcId}/accountSettings?activeTab=Taxation`)
                        )
                      }
                    >
                      Go to Taxation Mapping
                    </Link>
                  )}
                </Modal>
              </Can>
            </>
          )}
        </Box>
      </Box>

      {/* dialogs and panels */}
      <>
        {reminderDialogState.show && (
          <OkhatiDialog
            title="Warning"
            description={reminderDialogState.message}
            next={() => {
              finaliseInvoice({ openPrintWindow: reminderDialogState.openPrintWindow });
              setReminderDialogState({ show: false, message: "", openPrintWindow: false });
            }}
            cancel={() => {
              setReminderDialogState({ show: false, message: "", openPrintWindow: false });
            }}
            readMode={false}
          />
        )}

        {issueDateDialogState.show && (
          <OkhatiDialog
            title="Warning"
            description={
              <Typography>
                Bill <b>issue date</b> is different than today&apos;s date. <br /> Do you wish to
                continue ?
              </Typography>
            }
            next={() => {
              finaliseInvoice({ openPrintWindow: issueDateDialogState.openPrintWindow });
              setIssueDateDialogState({ show: false, openPrintWindow: false });
            }}
            cancel={() => {
              setIssueDateDialogState({ show: false, openPrintWindow: false });
            }}
            readMode={false}
          />
        )}

        {/* pay with qr panel */}
        {draft && canPayWithStaticQr && openQrPaymentPanel && (
          <QrPaymentPanel
            draft={draft}
            currentBillCounter={String(currentCounter)}
            selectedPaymentMethod={selectedPaymentMethod}
            onClose={() => setOpenQrPaymentPanel(false)}
          />
        )}

        {/* pay with nepal pay */}
        {draft && canPayWithNepalPayDynamicQr && openNepalPayDynamicQrPanel && (
          <NepalPayQrPanel
            draft={draft}
            currentBillCounter={String(currentCounter)}
            onClose={() => setOpenNepalPayynamicQrPanel(false)}
            selectedPaymentMethod={selectedPaymentMethod}
          />
        )}

        {/* pay with fonepay */}
        {draft && openFonepayDynamicQrPanel && (
          <FonepayDynamicQrPanel
            draft={draft}
            currentBillCounter={String(currentCounter)}
            onClose={() => setOpenFonepayDynamicQrPanel(false)}
            selectedPaymentMethod={selectedPaymentMethod}
          />
        )}
      </>
    </>
  );
};

export default connect(
  () => ({}),
  (dispatch: AppDispatch) => ({
    actions: {
      saveAsDraft: async (draft, setLoading) => {
        if (draft.summary.totalAmount >= 50_00_000) {
          dispatch(
            NotificationActions.notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: tl("billing.totalAmountExceeded"),
              autoTimeout: true
            })
          );
        } else {
          setLoading(true);
          await dispatch(billActions.saveAsDraft(draft));
          setLoading(false);
          dispatch(push(`/billing/bills?filter=draft`));
        }
      },
      saveAsProformaBill: async () => {
        dispatch(billActions.saveProformaBill());
      },
      cancelDraft: () => {
        dispatch(billActions.clearDraft());
        dispatch(push(`/billing/bills`));
      },
      finaliseAsBill: async (draft, openPrintWindow) => {
        if (draft.summary.totalAmount >= 50_00_000) {
          dispatch(
            NotificationActions.notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: tl("billing.totalAmountExceeded"),
              autoTimeout: true
            })
          );
        } else {
          await dispatch(billActions.finaliseDraft("a", openPrintWindow));
        }
      }
    }
  })
)(BillActions);
